import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Badge, Button, Col, Modal, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { InputGroup, FormControl } from "react-bootstrap";
import moment from "moment";
import CreatableSelect from "react-select/creatable";

import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { IDatasource } from "ag-grid-community";
import { DateRangeFloatingFilter } from "src/views/Docuclipper/AgGrid/DateRangeFloatingFilter";
import { datefilterParamsYYYYMMDD } from "../AnalyzeTransactionsTable";

import { ReduxState } from "../../../../redux";
import { createAlert } from "../../../../redux/reducers/Alerts";
import {
  createTransactionTag,
  deleteTransactionTag,
  getTransactionTags,
  getAllTransactionTags,
} from "../../../../docuclipper/api";
import ExternalFilter from "../../../Docuclipper/ExternalFilter";
import {
  clearExternalFilters,
  setExternalFiltersColumnDefs,
} from "../../../../redux/reducers/ExternalFilter";
import { useL1L2Params } from "../../../../hooks/useL1L2Params";
import { fetchTls } from "../../../../redux/reducers/TransactionManager";
import { useHasQbo } from "../../../../utils/utils";
import { convertTlsToTable } from "../AnalyzeTransactionsTable/utils";
import TagRegexesTransaction from "../Categorize/TagRegexesTransaction";

interface TagOption {
  value: string;
  label: string;
}

// Tag component for rendering tags in cells with CreatableSelect
const TagsRenderer = (props) => {
  const { data, context } = props;

  // Hooks must be called first, before any early returns
  const dispatch = useDispatch();
  const showAlert = (alert) => dispatch(createAlert(alert));
  const [showCreateTagModal, setShowCreateTagModal] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [pendingSelectedOptions, setPendingSelectedOptions] = useState<
    TagOption[] | null
  >(null);

  // Get job data for tag removal
  const { job } = useSelector((state: ReduxState) => state.JobData);

  // Get available tags from context (passed from parent)
  const availableTags = context?.availableTags || [];

  // Debug available tags
  //   console.log("TagsRenderer - availableTags:", availableTags);
  //   console.log("TagsRenderer - context:", context);

  const handleCreateNewTag = async () => {
    if (!newTagName.trim() || !pendingSelectedOptions) return;

    const trimmedTagName = newTagName.trim();

    // Add the new tag to the pending selection
    const filteredOptions = pendingSelectedOptions.filter(
      (option) => option.value !== "__CREATE_NEW_TAG__"
    );
    const updatedOptions = [
      ...filteredOptions,
      { value: trimmedTagName, label: trimmedTagName },
    ];

    // Close modal and reset state
    setShowCreateTagModal(false);
    setNewTagName("");
    setPendingSelectedOptions(null);

    // Continue with the tag change process
    await processTagChange(updatedOptions);

    // Refresh available tags to include the new one
    if (context?.refreshAllTags) {
      await context.refreshAllTags();
    }
  };

  const handleTagChange = async (selectedOptions) => {
    if (!data?.id) return;

    // Handle the special "Create new tag..." option
    const createTagOption = selectedOptions?.find(
      (option) => option.value === "__CREATE_NEW_TAG__"
    );
    if (createTagOption) {
      // Store the selected options and show modal
      setPendingSelectedOptions(selectedOptions);
      setShowCreateTagModal(true);
      return;
    }

    // Process normal tag changes
    await processTagChange(selectedOptions);
  };

  const processTagChange = async (selectedOptions) => {
    const newTags = selectedOptions
      ? selectedOptions.map((option) => option.value)
      : [];
    const currentTags = data.tags || [];

    // Find tags to add
    const tagsToAdd = newTags.filter((tag) => !currentTags.includes(tag));
    // Find tags to remove
    const tagsToRemove = currentTags.filter((tag) => !newTags.includes(tag));

    let hasError = false;
    let addedTags: string[] = [];
    let removedTags: string[] = [];

    // Add new tags
    for (const tag of tagsToAdd) {
      try {
        await createTransactionTag(data.id, { tag });
        addedTags.push(tag);
      } catch (err) {
        console.error("Error adding tag:", tag, err);
        hasError = true;
      }
    }

    // Remove tags using deleteTransactionTag
    if (tagsToRemove.length > 0) {
      try {
        // First get the transaction's tags with their IDs
        const transactionTags = await getTransactionTags(data.id);

        for (const tagName of tagsToRemove) {
          try {
            // Find the tag ID by name
            const tagToDelete = transactionTags.find(
              (tag) => (tag.name || tag.tag || tag) === tagName
            );

            if (tagToDelete && tagToDelete.id) {
              await deleteTransactionTag(tagToDelete.id);
              removedTags.push(tagName);
            }
          } catch (err) {
            console.error("Error removing tag:", tagName, err);
            hasError = true;
          }
        }
      } catch (err) {
        console.error("Error fetching transaction tags:", err);
        hasError = true;
      }
    }

    // Update the data object to reflect successful changes
    if (addedTags.length > 0 || removedTags.length > 0) {
      (data as any).tags = [
        ...currentTags.filter((tag) => !removedTags.includes(tag)),
        ...addedTags,
      ];

      // Refresh the grid to show updated data
      if (context && context.gridApi) {
        context.gridApi.refreshCells({ force: true });
      }
    }

    // Show appropriate message
    if (hasError) {
      showAlert({
        type: "warning",
        message: "Some tags could not be updated. Please try again.",
        timeout: 3000,
      });
    } else if (addedTags.length > 0 || removedTags.length > 0) {
      showAlert({
        type: "success",
        message: "Tags updated successfully",
        timeout: 3000,
      });
    }

    // Note: Available tags will refresh on next page load
  };

  // Handle undefined or invalid data after hooks
  if (!data || typeof data !== "object") {
    return <div className="text-muted">-</div>;
  }

  // Handle case where data doesn't have required properties
  if (!data.id) {
    console.warn("TagsRenderer: data missing id property", data);
    return <div className="text-muted">-</div>;
  }

  const currentTagValues = (data.tags || []).map((tag) => ({
    value: tag,
    label: tag,
  }));

  // Add the "Create new tag..." option at the top of the dropdown
  const optionsWithCreateNew = [
    {
      value: "__CREATE_NEW_TAG__",
      label: "➕ Create new tag...",
      isCreateOption: true,
    },
    ...availableTags,
  ];

  return (
    <div style={{ minWidth: "200px" }}>
      <CreatableSelect
        isMulti
        value={currentTagValues}
        options={optionsWithCreateNew}
        onChange={handleTagChange}
        placeholder="Select or create tags..."
        isClearable={false}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        styles={{
          container: (provided) => ({
            ...provided,
            fontSize: "12px",
            minWidth: "200px",
          }),
          control: (provided) => ({
            ...provided,
            minHeight: "24px",
            fontSize: "12px",
          }),
          valueContainer: (provided) => ({
            ...provided,
            padding: "0 4px",
          }),
          multiValue: (provided) => ({
            ...provided,
            fontSize: "11px",
          }),
          multiValueLabel: (provided) => ({
            ...provided,
            fontSize: "11px",
          }),
          menuPortal: (provided) => ({
            ...provided,
            zIndex: 9999,
          }),
          menu: (provided) => ({
            ...provided,
            zIndex: 9999,
          }),
          option: (provided, state) => ({
            ...provided,
            backgroundColor: state.data?.isCreateOption
              ? state.isFocused
                ? "#e6f3ff"
                : "#f8f9fa"
              : state.isFocused
              ? provided.backgroundColor
              : "white",
            color: state.data?.isCreateOption ? "#007bff" : provided.color,
            fontWeight: state.data?.isCreateOption ? "500" : "normal",
            borderBottom: state.data?.isCreateOption
              ? "1px solid #dee2e6"
              : "none",
            marginBottom: state.data?.isCreateOption ? "4px" : "0",
          }),
        }}
        formatCreateLabel={(inputValue) => `Create "${inputValue}"`}
        onMenuOpen={() => {}}
        onMenuClose={() => {}}
      />

      {/* Create New Tag Modal */}
      <Modal
        show={showCreateTagModal}
        onHide={() => {
          setShowCreateTagModal(false);
          setNewTagName("");
          setPendingSelectedOptions(null);
        }}
      >
        <div className="modal-header">
          <h5 className="modal-title">Create New Tag</h5>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => {
              setShowCreateTagModal(false);
              setNewTagName("");
              setPendingSelectedOptions(null);
            }}
          />
        </div>
        <Modal.Body>
          <InputGroup>
            <FormControl
              type="text"
              placeholder="Enter tag name..."
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleCreateNewTag();
                }
              }}
              autoFocus
            />
          </InputGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowCreateTagModal(false);
              setNewTagName("");
              setPendingSelectedOptions(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateNewTag}
            disabled={!newTagName.trim()}
          >
            Create Tag
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

interface Props {}

export const TransactionWithTagsViewer: React.FC<Props> = () => {
  const dispatch = useDispatch();
  const { l1, l2 } = useL1L2Params();
  const [showRulesModal, setShowRulesModal] = useState(false);
  const gridRef = useRef(null) as any;
  const gridApi = useRef(null) as any;
  const [allTags, setAllTags] = useState<string[]>([]);
  const [gridReady, setGridReady] = useState(false);
  const datasourceSetRef = useRef(false);

  // Tags will be collected from getAllTransactionTags API
  const hasQbo = useHasQbo();

  // Get necessary state from Redux
  const { job } = useSelector((state: ReduxState) => state.JobData);
  const { data: jobDocs } = useSelector(
    (state: ReduxState) => state.JobDocument
  );
  const { pagination, accountChunkLabels } = useSelector(
    (state: ReduxState) => state.TransactionManager
  );
  const { externalFilters } = useSelector(
    (state: ReduxState) => state.ExternalFilter
  );

  // Function to refresh all tags
  const refreshAllTags = useCallback(async () => {
    try {
      const response = await getAllTransactionTags();
      const tagsArray = (response as any).tags || response; // Handle both object and array responses
      const tagNames = tagsArray.map((tag) => tag.name || tag.tag || tag);
      setAllTags(tagNames);
    } catch (err) {
      console.error("Error fetching all transaction tags:", err);
      setAllTags([]);
    }
  }, []);

  // Fetch all available tags for filtering
  useEffect(() => {
    refreshAllTags();
  }, [refreshAllTags]);

  const externalFilterColumns = useMemo(
    () => [
      {
        field: "account",
        label: "Account",
        filter: true,
        filterType: "text",
        defaultFilterType: "contains",
      },
      {
        field: "date",
        label: "Date",
        filter: true,
        filterType: "date",
        defaultFilterType: "equals",
      },
      {
        field: "description",
        label: "Description",
        filter: true,
        filterType: "text",
        defaultFilterType: "contains",
      },
      {
        field: "amount",
        label: "Amount",
        filter: true,
        filterType: "number",
        defaultFilterType: "equals",
      },
      {
        field: "tags",
        label: "Tags",
        filter: true,
        filterType: "select",
        options: (() => {
          const tagOptions = allTags.map((tag) => ({
            label: tag,
            value: tag,
          }));
          return tagOptions;
        })(),
        defaultFilterType: "equals",
      },
    ],
    [allTags]
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Account",
        field: "account",
        width: 150,
        filter: true,
        sortable: true,
      },
      {
        headerName: "Date",
        field: "date",
        minWidth: 200,
        filter: "agDateColumnFilter",
        filterParams: datefilterParamsYYYYMMDD,
        floatingFilterComponent: DateRangeFloatingFilter,
        floatingFilterComponentParams: { suppressFilterButton: true },
        sortable: true,
        valueFormatter: (params) => {
          if (params.value) {
            return moment(params.value).format("MM/DD/YYYY");
          }
          return "";
        },
      },
      {
        headerName: "Description",
        field: "description",
        width: 250,
        filter: true,
        sortable: true,
      },
      {
        headerName: "Amount",
        field: "amount",
        width: 120,
        filter: "agNumberColumnFilter",
        sortable: true,
        valueFormatter: (params) => {
          if (params.value !== null && params.value !== undefined) {
            return `$${parseFloat(params.value).toFixed(2)}`;
          }
          return "";
        },
      },
      {
        headerName: "Tags",
        field: "tags",
        width: 350,
        cellRenderer: TagsRenderer,
        sortable: false,
        filter: "agTextColumnFilter",
        suppressMenu: true, // Hide the built-in filter menu, but keep the filter functionality
      },
    ],
    []
  );

  // Set up external filter columns
  useEffect(() => {
    dispatch(clearExternalFilters());
    dispatch(
      setExternalFiltersColumnDefs({
        externalFilterColumnDefs: externalFilterColumns,
      })
    );
  }, [dispatch, externalFilterColumns, allTags]);

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      filter: true,
    }),
    []
  );

  const onGridReady = useCallback((params) => {
    // Store the grid API reference
    gridRef.current = params;
    gridApi.current = params.api;
    params.api.sizeColumnsToFit();

    // Reset datasource flag for new grid instance
    datasourceSetRef.current = false;

    // Set grid ready state to trigger datasource setup
    // Use setTimeout to ensure the grid is fully initialized
    setTimeout(() => {
      setGridReady(true);
    }, 0);
  }, []);

  // Set up infinite row model for AG Grid
  useEffect(() => {
    // Wait for grid to be ready
    if (!gridReady || !gridRef.current?.api) {
      return;
    }

    // Only proceed if we have essential data - either job OR l1+l2
    if (!job?.id && !(l1 && l2)) {
      return;
    }

    const dataSource: IDatasource = {
      rowCount: undefined,
      getRows: (params) => {
        const convertFilterModel = () => {
          return Object.entries(params.filterModel).map(
            ([field, filter]: [string, any]) => {
              const filterType = filter.filterType || "text";
              const operationType = filter.type;

              const filterOutput = {
                field,
                operator: operationType,
                valueFrom: undefined,
                valueTo: undefined,
                value: undefined,
              };

              if (filterType === "number") {
                if (operationType === "inRange") {
                  filterOutput.valueFrom = filter.filter;
                  filterOutput.valueTo = filter.filterTo;
                } else if (
                  operationType === "blank" ||
                  operationType === "notBlank"
                ) {
                  filterOutput.value = undefined;
                } else {
                  filterOutput.value = filter.filter;
                }
              } else if (filterType === "date") {
                if (operationType === "inRange") {
                  filterOutput.valueFrom = filter.dateFrom;
                  filterOutput.valueTo = filter.dateTo;
                } else if (
                  operationType === "blank" ||
                  operationType === "notBlank"
                ) {
                  filterOutput.value = undefined;
                } else {
                  filterOutput.value = filter.dateFrom;
                }
              } else {
                filterOutput.value = filter.filter;
              }

              return filterOutput;
            }
          );
        };

        const convertSortModel = (sortModel: any[]) => {
          return sortModel.map((sort) => ({
            field: sort.colId,
            direction: sort.sort.toUpperCase(),
          }));
        };

        const convertExternalFilters = () => {
          return Object.entries(externalFilters).map(
            ([field, filter]: [string, any]) => {
              const filterOutput = {
                field,
                operator: filter.filterType || filter.type,
                valueFrom: undefined,
                valueTo: undefined,
                value: undefined,
              };

              if (filter.filterType === "number") {
                if (filter.filterType === "inRange") {
                  filterOutput.valueFrom = filter.filterValue;
                  filterOutput.valueTo = filter.filterValueTo;
                } else {
                  filterOutput.value = filter.filterValue;
                }
              } else if (filter.filterType === "date") {
                if (filter.filterType === "inRange") {
                  filterOutput.valueFrom = filter.filterValue;
                  filterOutput.valueTo = filter.filterValueTo;
                } else {
                  filterOutput.value = filter.filterValue;
                }
              } else {
                // Handle text and select filters
                filterOutput.value = filter.filterValue;
              }

              return filterOutput;
            }
          );
        };

        dispatch(
          fetchTls(false, {
            pagination: {
              page: Math.floor(params.startRow / pagination.pageSize) + 1,
              pageSize: params.endRow - params.startRow,
            },
            filters: [
              {
                field: "fieldName",
                operator: "equals" as const,
                value: "bankMode",
              },
              // Don't filter by included to show all transactions
              ...convertFilterModel(),
              ...convertExternalFilters(),
            ],
            sorting: convertSortModel(params.sortModel),
            successCallback: (rowsThisPage, lastRow) => {
              // Call the original success callback
              params.successCallback(rowsThisPage, lastRow);
            },
            accountChunkLabels,
            documents: jobDocs || [],
            hasQbo,
            job,
            l1,
            l2,
          })
        );
      },
    };

    if (gridRef.current?.api && dataSource && !datasourceSetRef.current) {
      gridRef.current.api.setDatasource(dataSource);
      datasourceSetRef.current = true;
    }
  }, [
    gridReady,
    dispatch,
    pagination.pageSize,
    accountChunkLabels,
    jobDocs,
    hasQbo,
    job,
    l1,
    l2,
    externalFilters,
  ]);

  return (
    <div className="transaction-tags-viewer">
      <Row className="mb-3">
        <Col>
          <h2>Transaction Tagging</h2>
          <p className="text-muted mb-3">
            Tag transactions for better organization. Create automated rules
            based on amounts, descriptions, and more.
          </p>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <div className="d-flex justify-content-end align-items-center">
            <Button
              variant="outline-primary"
              onClick={() => setShowRulesModal(true)}
            >
              <FontAwesomeIcon icon="edit" />
              <span className="ml-1">Edit Tagging Rules</span>
            </Button>
          </div>
        </Col>
      </Row>

      <Row className="mb-2">
        <Col md="12">
          Filter by
          <ExternalFilter
            columnDefs={externalFilterColumns}
            gridApi={gridReady ? gridApi.current : null}
          />
        </Col>
      </Row>

      <Row>
        <Col>
          <div
            className="ag-theme-alpine"
            style={{ height: "600px", width: "100%" }}
          >
            <AgGridReact
              ref={gridRef}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              rowSelection="multiple"
              suppressRowClickSelection
              animateRows
              rowModelType="infinite"
              onGridReady={onGridReady}
              context={{
                gridApi: gridReady ? gridApi.current : null,
                availableTags: allTags.map((tag) => ({
                  value: tag,
                  label: tag,
                })),
                refreshAllTags: refreshAllTags,
              }}
              pagination={true}
              paginationPageSize={10}
              cacheBlockSize={10}
              infiniteInitialRowCount={1}
              maxBlocksInCache={10}
            />
          </div>
        </Col>
      </Row>

      {/* Transaction Rules Modal */}
      <TagRegexesTransaction
        showModal={showRulesModal}
        onToggleModal={() => setShowRulesModal(false)}
        integrationId={null}
      />
    </div>
  );
};
